import { useQuery } from '@tanstack/react-query';
import type { SettlementFormData } from '../../settlement/components/SettlementInfoForm/types';
import { accountEditQueries } from '../query-factory';
import { transformAccountInfoToFormData } from '../utils/transformAccountInfoToFormData';

/**
 * 정산정보 조회 및 Form 연동 Hook
 * 기존 정산정보를 조회하여 Form defaultValues로 변환
 *
 * @param enabled 정산정보 풀데이터(GET /v2/accountInfo) 조회 활성화 여부.
 *   29CM 미등록 사용자는 이 호출이 실패('불러오기 실패' 에러)하므로, 등록/안내 분기에서는
 *   false 로 호출을 막고 수정 시에만 조회한다. (CMQA-7095)
 */
export const useAccountInfoForm = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const {
    data: accountInfo,
    isPending,
    error,
    isError,
  } = useQuery({
    ...accountEditQueries.accountInfo.detail,
    enabled,
  });

  const formDefaultValues: Partial<SettlementFormData> | undefined = accountInfo
    ? transformAccountInfoToFormData(accountInfo)
    : undefined;

  const accountDisplayInfo = accountInfo
    ? {
        bankName: accountInfo.bankName ?? '',
        accountNumber: accountInfo.bankAccountNumber ?? '',
        accountHolder: accountInfo.bankOwnerName ?? '',
      }
    : undefined;

  const hasExistingAccountInfo = Boolean(
    accountInfo?.bankAccountNumber || accountInfo?.bankOwnerName,
  );

  return {
    accountInfo,
    formDefaultValues,
    accountDisplayInfo,
    hasExistingAccountInfo,
    isPending,
    error,
    isError,
  };
};
